/**
 * Runtime Configuration Utility
 *
 * This module provides access to runtime environment variables that are injected
 * at container startup. It supports both runtime injection (production) and
 * build-time values (development).
 *
 * Priority:
 * 1. Runtime injected values (window.RUNTIME_CONFIG) - for production
 * 2. Build-time values (import.meta.env.VITE_*) - for development
 * 3. Hardcoded fallbacks - for local development without env vars
 */

interface RuntimeConfig {
  BACKEND_URL: string;
  AZURE_CLIENT_ID: string;
  AZURE_TENANT_ID: string;
  AZURE_REDIRECT_URI: string;
  AZURE_OPENAI_ENDPOINT?: string;
  AZURE_OPENAI_DEPLOYMENT?: string;
  AZURE_OPENAI_API_VERSION?: string;
}

// Extend the Window interface to include RUNTIME_CONFIG
declare global {
  interface Window {
    RUNTIME_CONFIG?: RuntimeConfig;
  }
}

/**
 * Get a configuration value with the following priority:
 * 1. Runtime injected value (window.RUNTIME_CONFIG)
 * 2. Build-time environment variable (import.meta.env.VITE_*)
 * 3. Fallback value
 */
function getConfigValue(
  runtimeKey: keyof RuntimeConfig,
  buildTimeEnvVar: string,
  fallback: string
): string {
  // Priority 1: Runtime injected value
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG[runtimeKey]) {
    const value = window.RUNTIME_CONFIG[runtimeKey];
    // Don't use the value if it's still a template variable (e.g., '${BACKEND_URL}')
    if (value && !value.startsWith('${')) {
      return value;
    }
  }

  // Priority 2: Build-time environment variable
  const buildTimeValue = import.meta.env[buildTimeEnvVar];
  if (buildTimeValue) {
    return buildTimeValue;
  }

  // Priority 3: Fallback value
  return fallback;
}

/**
 * Get the backend API URL
 */
export const getBackendUrl = (): string => {
  return getConfigValue(
    'BACKEND_URL',
    'VITE_BACKEND_URL',
    'http://localhost:8000/api'
  );
};

/**
 * Get Azure Entra ID Client ID
 */
export const getAzureClientId = (): string => {
  return getConfigValue(
    'AZURE_CLIENT_ID',
    'VITE_AZURE_CLIENT_ID',
    ''
  );
};

/**
 * Get Azure Entra ID Tenant ID
 */
export const getAzureTenantId = (): string => {
  return getConfigValue(
    'AZURE_TENANT_ID',
    'VITE_AZURE_TENANT_ID',
    ''
  );
};

/**
 * Get Azure Entra ID Redirect URI
 */
export const getAzureRedirectUri = (): string => {
  return getConfigValue(
    'AZURE_REDIRECT_URI',
    'VITE_AZURE_REDIRECT_URI',
    'http://localhost:5173'
  );
};

/**
 * Get Azure OpenAI Endpoint
 */
export const getAzureOpenAIEndpoint = (): string | undefined => {
  const value = getConfigValue(
    'AZURE_OPENAI_ENDPOINT',
    'VITE_AZURE_OPENAI_ENDPOINT',
    ''
  );
  return value || undefined;
};

/**
 * Get Azure OpenAI Deployment Name
 */
export const getAzureOpenAIDeployment = (): string | undefined => {
  const value = getConfigValue(
    'AZURE_OPENAI_DEPLOYMENT',
    'VITE_AZURE_OPENAI_DEPLOYMENT',
    ''
  );
  return value || undefined;
};

/**
 * Get Azure OpenAI API Version
 */
export const getAzureOpenAIAPIVersion = (): string => {
  return getConfigValue(
    'AZURE_OPENAI_API_VERSION',
    'VITE_AZURE_OPENAI_API_VERSION',
    '2025-02-15-preview'
  );
};

/**
 * Get all runtime configuration
 */
export const getRuntimeConfig = (): RuntimeConfig => {
  return {
    BACKEND_URL: getBackendUrl(),
    AZURE_CLIENT_ID: getAzureClientId(),
    AZURE_TENANT_ID: getAzureTenantId(),
    AZURE_REDIRECT_URI: getAzureRedirectUri(),
    AZURE_OPENAI_ENDPOINT: getAzureOpenAIEndpoint(),
    AZURE_OPENAI_DEPLOYMENT: getAzureOpenAIDeployment(),
    AZURE_OPENAI_API_VERSION: getAzureOpenAIAPIVersion(),
  };
};

/**
 * Log runtime configuration (for debugging)
 * IMPORTANT: Only call this in development, never in production
 */
export const logRuntimeConfig = () => {
  if (import.meta.env.DEV) {
    console.log('📋 Runtime Configuration:', {
      source: window.RUNTIME_CONFIG ? 'Runtime Injected' : 'Build-time',
      config: {
        backendUrl: getBackendUrl(),
        azureClientId: getAzureClientId().substring(0, 8) + '...',
        azureTenantId: getAzureTenantId().substring(0, 8) + '...',
        azureRedirectUri: getAzureRedirectUri(),
        openAIEndpoint: getAzureOpenAIEndpoint(),
        openAIDeployment: getAzureOpenAIDeployment(),
      },
    });
  }
};
